import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ValueData } from '@/types/project';
import { formatCurrency } from '@/utils/format';

interface ValueFormProps {
  value: ValueData;
  onChange: (field: keyof ValueData, value: number | null) => void;
}

export function ValueForm({ value, onChange }: ValueFormProps) {
  const [retentionError, setRetentionError] = useState<string | null>(null);

  const totalValue = (value.contractValue || 0) +
    (value.approvedVariations || 0) +
    (value.unapprovedVariations || 0);

  const fields = [
    { id: 'contractValue', label: 'Contract Value', unit: '£' },
    { id: 'approvedVariations', label: 'Approved Variations', unit: '£' },
    { id: 'unapprovedVariations', label: 'Unapproved Variations', unit: '£' },
    { id: 'invoicedToDate', label: 'Invoiced to Date', unit: '£' },
    { id: 'retention', label: 'Retention', unit: '%' },
    { id: 'forecastedRevenue', label: 'Forecasted Value', unit: '£' },
  ] as const;

  const handleChange = (id: keyof ValueData, rawValue: string) => {
    const numValue = rawValue ? Number(rawValue) : null;

    if (id === 'retention' && numValue !== null) {
      if (numValue < 0 || numValue > 100) {
        setRetentionError('Retention must be between 0 and 100');
        return;
      }
    }

    if (id === 'retention') {
      setRetentionError(null);
    }

    onChange(id, numValue);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Value</CardTitle>
        <div className="text-sm text-muted-foreground">
          Total: {formatCurrency(totalValue)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fields.map(({ id, label, unit }) => (
            <div key={id} className="space-y-2">
              <Label htmlFor={id}>{label} ({unit})</Label>
              <Input
                id={id}
                type="number"
                value={value[id] ?? ''}
                onChange={(e) => handleChange(id, e.target.value)}
                placeholder={`Enter ${label.toLowerCase()}`}
                className="text-right"
              />
              {id === 'retention' && retentionError && (
                <p className="text-sm text-red-500">{retentionError}</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
